import React from "react";
import { Controller, useWatch } from "react-hook-form";
import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { businessRuleOptions } from "../data/businessRuleOptions";

const headCellSx = {
  fontWeight: 700,
  fontSize: "12px",
  color: "text.secondary",
  whiteSpace: "nowrap",
  py: 1,
};

const bodyCellSx = { py: 0.5, px: 1 };

const ROW_FIELDS = ["nomenclature", "wageItemType", "irrf", "inss", "fgts", "averages", "incorporateSalary"];

const AmountGridTable = ({
  control,
  errors,
  rows,
  onAddRow,
  onDeleteRow,
  fieldNamespace = "amountAmountRows",
  isValidated = false,
}) => {
  const fieldValues = useWatch({ control, name: fieldNamespace });

  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const isLastRowComplete =
    !lastRow ||
    ROW_FIELDS.every(f => {
      const val = fieldValues?.[lastRow.id]?.[f];
      return val !== undefined && val !== "" && val !== null;
    });

  return (
  <div style={{ width: "100%" }}>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>NOMENCLATURA <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={headCellSx}>TIPO <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={headCellSx}>IRRF <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={headCellSx}>INSS <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={headCellSx}>FGTS <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={headCellSx}>MÉDIAS <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={headCellSx}>INCORPORA SALÁRIO <span style={{ color: "red" }}>*</span></TableCell>
            <TableCell sx={{ ...headCellSx, width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id}>
              <TableCell sx={bodyCellSx}>
                <Controller
                  name={`${fieldNamespace}.${row.id}.nomenclature`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      disabled={isValidated}
                      size="small"
                      fullWidth
                      required
                      error={!!errors?.[fieldNamespace]?.[row.id]?.nomenclature}
                      helperText={errors?.[fieldNamespace]?.[row.id]?.nomenclature?.message}
                      sx={{ minWidth: 160 }}
                    />
                  )}
                />
              </TableCell>

              <TableCell sx={bodyCellSx}>
                <Controller
                  name={`${fieldNamespace}.${row.id}.wageItemType`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      fullWidth
                      required
                      error={!!errors?.[fieldNamespace]?.[row.id]?.wageItemType}
                      sx={{ minWidth: 120 }}
                    >
                      <Select {...field} disabled={isValidated} required>
                        {businessRuleOptions.wageItemType.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {errors?.[fieldNamespace]?.[row.id]?.wageItemType?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </TableCell>

              {["irrf", "inss", "fgts", "averages", "incorporateSalary"].map(colKey => (
                <TableCell key={colKey} sx={bodyCellSx}>
                  <Controller
                    name={`${fieldNamespace}.${row.id}.${colKey}`}
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        size="small"
                        fullWidth
                        required
                        error={!!errors?.[fieldNamespace]?.[row.id]?.[colKey]}
                        sx={{ minWidth: 90 }}
                      >
                        <Select {...field} disabled={isValidated} required>
                          {businessRuleOptions.amountTable.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors?.[fieldNamespace]?.[row.id]?.[colKey]?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </TableCell>
              ))}

              <TableCell sx={bodyCellSx} align="center">
                <IconButton
                  disabled={Boolean(isValidated)}
                  onClick={() => onDeleteRow(row.id)}
                  color="error"
                  size="small"
                  aria-label="delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Button
      variant="outlined"
      size="small"
      disabled={Boolean(isValidated) || !isLastRowComplete}
      onClick={onAddRow}
      sx={{ mt: 1.5 }}
    >
      Adicionar linha
    </Button>
    {typeof errors?.[fieldNamespace]?.message === "string" && (
      <FormHelperText error sx={{ mx: 0, mt: 1 }}>
        {errors[fieldNamespace].message}
      </FormHelperText>
    )}
  </div>
  );
};

export default AmountGridTable;
